import React, { useState, useRef } from 'react';
import { Play, Pause, Volume2 } from 'lucide-react';
import { motion } from 'framer-motion';
function AudioCard({
  name,
  image,
  audioSrc,
  delay
}: {
  name: string;
  image: string;
  audioSrc: string;
  delay: number;
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        // Pausar otros audios si es necesario (opcional, pero buena práctica)
        document.querySelectorAll('audio').forEach(el => {
          if (el !== audioRef.current) {
            el.pause();
          }
        });
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const current = audioRef.current.currentTime;
      const duration = audioRef.current.duration;
      if (duration > 0) {
        setProgress(current / duration * 100);
      }
    }
  };
  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(0);
  };
  // Escuchar cuando otro audio se reproduce para pausar este
  const handlePause = () => {
    setIsPlaying(false);
  };
  return <motion.div initial={{
    opacity: 0,
    scale: 0.95
  }} whileInView={{
    opacity: 1,
    scale: 1
  }} viewport={{
    once: true
  }} transition={{
    delay
  }} className="bg-gradient-to-br from-[#1a1510] to-[#0a0a0a] border border-[#D4AF37]/40 rounded-3xl overflow-hidden shadow-[0_0_60px_rgba(212,175,55,0.15)] flex flex-col md:flex-row relative w-full" data-id="element-58">
      {/* Image Side */}
      <div className="w-full md:w-2/5 h-[350px] md:h-auto relative" data-id="element-59">
        <img src={image} alt={name} className="absolute inset-0 w-full h-full object-cover" data-id="element-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1510] via-transparent to-transparent md:bg-gradient-to-r" data-id="element-61" />
      </div>

      {/* Player Side */}
      <div className="w-full md:w-3/5 p-8 sm:p-10 flex flex-col justify-center relative" data-id="element-62">
        <div className="flex items-center gap-3 mb-2" data-id="element-63">
          <Volume2 className="w-5 h-5 text-[#D4AF37]" data-id="element-64" />
          <span className="font-inter text-[#D4AF37] text-xs font-bold uppercase tracking-widest" data-id="element-65">
            Testimonio de Audio
          </span>
        </div>

        <h3 className="font-playfair text-3xl text-white mb-2" data-id="element-66">
          La historia de {name}
        </h3>
        <p className="font-inter text-gray-400 text-sm mb-10" data-id="element-67">
          Clienta verificada • Limpia de Mal de Ojo
        </p>

        {/* Custom Player Controls */}
        <div className="flex flex-col gap-6" data-id="element-68">
          <div className="flex items-center gap-6" data-id="element-69">
            <button onClick={togglePlay} className="w-16 h-16 shrink-0 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#B8860B] flex items-center justify-center text-black hover:scale-105 transition-transform shadow-[0_0_25px_rgba(212,175,55,0.5)]" data-id="element-70">
              {isPlaying ? <Pause className="w-8 h-8 fill-black" data-id="element-71" /> : <Play className="w-8 h-8 fill-black ml-1" data-id="element-72" />}
            </button>

            {/* Progress Bar */}
            <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden relative" data-id="element-73">
              <motion.div className="absolute top-0 left-0 h-full bg-[#D4AF37]" style={{
              width: `${progress}%`
            }} data-id="element-74" />
            </div>
          </div>

          <p className="font-inter text-xs text-gray-400 text-center md:text-left" data-id="element-75">
            {isPlaying ? 'Reproduciendo testimonio...' : 'Presioná play para escuchar'}
          </p>
        </div>

        {/* Hidden Audio Element */}
        <audio ref={audioRef} src={audioSrc} onTimeUpdate={handleTimeUpdate} onEnded={handleEnded} onPause={handlePause} className="hidden" data-id="element-76" />
      </div>
    </motion.div>;
}
export function AudioTestimonial() {
  return <section className="w-full py-24 px-4 bg-[#050505] relative overflow-hidden" data-id="element-77">
      {/* Decorative Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#D4AF37]/10 rounded-full blur-[120px] pointer-events-none" data-id="element-78" />

      <div className="max-w-4xl mx-auto relative z-10 flex flex-col gap-12" data-id="element-79">
        {/* Intro Text for Valentina */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-4" data-id="element-80">
          <p className="font-playfair text-xl sm:text-2xl md:text-3xl text-white leading-relaxed max-w-3xl mx-auto" data-id="element-81">
            "Valentina llevaba año y medio sin entender por qué todo le salía
            mal. Perdió su trabajo, casi pierde su relación, y ningún médico le
            daba respuesta. Hasta que alguien le dijo lo que ella no quería
            escuchar.{' '}
            <span className="text-[#D4AF37] italic font-semibold" data-id="element-82">
              Escuchá su historia 👇
            </span>
            "
          </p>
        </motion.div>

        {/* Valentina's Audio Card */}
        <AudioCard name="Valentina" image="https://cdn.magicpatterns.com/uploads/pCjLPvCwWqTMLQh4CqgQdF/ce2b7058-2f1c-48f8-bc67-aa174cbdeb92.png" audioSrc="https://cdn.magicpatterns.com/uploads/vMpW1gYD95Y5dBpPQnkg8a/202604281105.mp4" delay={0.2} data-id="element-83" />

        {/* Carolina's Audio Card (No intro text as requested) */}
        <AudioCard name="Carolina" image="https://cdn.magicpatterns.com/uploads/uMDLx9TjmqnFjirwt7VQqH/94db5963-c261-476f-8cf0-96f32ea9fabd.png" audioSrc="https://cdn.magicpatterns.com/uploads/p3rvn3PZ2sns7FxWceWfvS/202604281105 (2).mp4" delay={0.3} data-id="element-84" />
      </div>
    </section>;
}